import { CheckboxComponent } from '../../UI/CheckboxComponent/CheckboxComponent';
import { MenuItemComponent } from '../../UI/MenuItemComponent/MenuItemComponent';
import './FilterComponent.scss';
import { FilterState, handleToolTipClick, orderFiltersAlphabetically } from './model';
import { FC, useEffect, useRef, useState, useCallback } from 'react';
import {
	FilterLevel,
	FilterType,
	FilterVM,
	PintoFilters,
	ScreenType,
	viewModeType
} from '../../../common/view-model';
import { RootState } from '../../../redux/AppReducer';
import { useWindowSize } from '../../../hooks/useWindowSize';
import { FilterTooltipComponent } from '../tooltip/FilterTooltipComponent';
import { FilterActions } from '../../../redux/FilterActions';
import { handleTracking } from '../../../helpers/handelTracking';
import { v4 as uuidv4 } from 'uuid';
import { TrackingEventStatus } from '../../../common/view-model';
import { getFilterTypeKeyForUrl } from '../../../helpers/urlParametersHelpers';
import { CategoryFilterComponent } from '../category-filter/CategoryFilterComponent';
import { isCurrentUserAnonymous } from '../../../helpers/authenticationHelper';
import LockOverlayComponent from '../../LockOverlay/LockOverlayComponent';
import { AppActions } from '../../../redux/AppActions';
import { UserActions } from '../../../redux/UserActions';
import { publicProductDiscoveryActions } from '../../../common/constants';
import { useAppDispatch } from '../../../redux/store';
import { useSelector } from 'react-redux';
import { WhatsNewFilterElementsComponent } from '../whats-new/whats-new-filter-elements/WhatsNewFilterElementsComponent';
import { ShowParticipationFilterComponent } from '../show-participation/ShowParticipationFilterComponent';
interface IMainFilterComponentProps {
	filter: PintoFilters;
	openIndex: number | undefined;
	parentIndex: number;
	setOpenIndex: (index: number | undefined) => void;
	deviceScreen?: ScreenType;
	isMobile?: boolean;
	pendingFilters?: FilterVM[];
	setPendingFilters?: React.Dispatch<React.SetStateAction<FilterVM[]>>;
	selectedFilters?: FilterVM[];
}
interface IFilterComponentProps {
	onClose: () => void;

	setCurrentCategory: (category?: string) => void;
	currentCategory: string;
	productsPageUrl: string;
	setLockModalContent: () => void;
	registerUserClickEvent: (event: string) => void;
	clearAllFilters: () => void;
}
export const MainFilterComponent: FC<IMainFilterComponentProps> = ({
	filter,
	openIndex,
	setOpenIndex,
	parentIndex,
	deviceScreen,
	pendingFilters,
	setPendingFilters
}) => {
	const dispatch = useAppDispatch();
	const [filterComponentState] = useState({ ...FilterState, filter });
	filterComponentState.multifilterItems = [];
	const size = useWindowSize();
	const [deviceViewMode, setDeviceViewMode] = useState<viewModeType>(
		size.width > 768 ? viewModeType.DESKTOP : viewModeType.MOBILE
	);
	const { currentFilterOpenIndex } = useSelector(
		(state: RootState) => state.appState.filterState || {}
	);
	const { selectedFilters, openFilterFunctionRef, carrotIconClicked } = useSelector(
		(state: RootState) => state.appState.filterState || {}
	);
	const { user } = useSelector((state: RootState) => state.appState.userState || {});

	const filterRef = useRef<HTMLDivElement>(null);
	const filterContainerRef = useRef<HTMLDivElement>(null);
	const maxScrollHeigth = 520;
	const [isDeskTopScreen, setIsDeskTopScreen] = useState<boolean>(
		deviceScreen === ScreenType.LARGE_DESKTOP ||
			deviceScreen === ScreenType.MEDIUM_DESKTOP ||
			deviceScreen === ScreenType.SMALL_DESKTOP
	);
	const isMobile = deviceScreen === ScreenType.MOBILE ? true : false;
	const setOpenFilterRef = (setOpen: () => void) =>
		dispatch(FilterActions.addOpenFilterFunctionRef(setOpen));
	const setBubbleFuction = (type: FilterType, value: () => void, filterRef: any) =>
		dispatch(FilterActions.setBubbleFunctionRef({ type, value, filterRef }));
	const clearBubbleFunctionRef = () => dispatch(FilterActions.clearBubbleFunctionRef());
	const carrotIconClickCallback = useCallback(
		(carrotIconOpen: boolean) => {
			dispatch(FilterActions.setCarrotIconClick(carrotIconOpen));
		},
		[dispatch]
	);
	const setLockModalContent = () =>
		dispatch(
			AppActions.setModalContent({
				content: <LockOverlayComponent />,
				showCloseIcon: true,
				canScroll: false,
				closeOnDocumentClick: true
			})
		);
	const setHasClickOnFilter = (hasClicked: boolean) =>
		dispatch(UserActions.clickOnFilter(hasClicked));
	const registerUserClickEvent = (event: string) =>
		dispatch(UserActions.registerUserClickEvent(event));
	const handleClick = (index: number | undefined, filterName: string | undefined) => {
		if (isCurrentUserAnonymous() && openIndex !== index) {
			registerUserClickEvent(publicProductDiscoveryActions.CLICK_OPEN_FILTER);
		}
		setOpenIndex(openIndex === index ? undefined : index);
		if (openIndex !== index) {
			handleTracking({
				transactionId: uuidv4(),
				entityId: filter.id!,
				status: TrackingEventStatus.SUCCESS,
				type: `Click on ${filterName}`,
				timeStamp: new Date().toDateString(),
				eventPayload: {
					id: filter.id!,
					name: filter.name!,
					type: getFilterTypeKeyForUrl(filter.type!),
					url: window.location.href,
					tags: [],
					metadata: {
						user_id: user?.id,
						user_type: user?.user_type,
						environment: import.meta.env.VITE_ENV_NAME,
						filter_id: filter.id,
						filter_name: filter.name
					}
				}
			});
		}
	};
	const onTooltipClick = (value: string, iconId: string) => {
		handleToolTipClick(value, iconId);
		carrotIconClickCallback(true);
	};
	const handleAnonymousClick = () => {
		setHasClickOnFilter(true);
		setLockModalContent();
	};
	const isCategoryFilter = filter.type === FilterType.CATEGORY;
	const isOpen = isCategoryFilter || openIndex === parentIndex;
	useEffect(() => {
		if (size.width < 768) {
			setDeviceViewMode(viewModeType.MOBILE);
		} else {
			setDeviceViewMode(viewModeType.DESKTOP);
		}
	}, [size]);
	useEffect(() => {
		if (filter.type === FilterType.CATEGORY) {
			setOpenFilterRef(() => setOpenIndex(parentIndex));
		}
	}, [filter.type, parentIndex]);
	useEffect(() => {
		if (!selectedFilters || selectedFilters.length === 0) return;

		const lastFilter = selectedFilters[selectedFilters.length - 1];

		if (
			lastFilter.type === FilterType.CATEGORY ||
			lastFilter.type === FilterType.WHATS_NEW ||
			lastFilter.type === FilterType.NEW_TO_BEACON ||
			lastFilter.type === FilterType.NEW_TO_MARKET ||
			lastFilter.type === FilterType.NEXTY_FINALISTS
		) {
			openFilterFunctionRef?.();
		}
	}, [selectedFilters, openFilterFunctionRef]);
	useEffect(() => {
		if (
			carrotIconClicked &&
			openIndex === parentIndex &&
			filterRef?.current &&
			filterRef.current.scrollHeight > maxScrollHeigth
		) {
			filterRef.current.classList.add('scrollable');
		} else {
			filterRef.current?.classList.remove('scrollable');
		}
	}, [openIndex, parentIndex, carrotIconClicked]);
	useEffect(() => {
		if (!filterContainerRef.current) return;
		if (parentIndex === 0 && setBubbleFuction) {
			setBubbleFuction(FilterType.NEW_TO_MARKET, () => setOpenIndex(0), null);
			setBubbleFuction(FilterType.NEW_TO_BEACON, () => setOpenIndex(0), null);
			setBubbleFuction(FilterType.NEXTY_FINALISTS, () => setOpenIndex(0), null);
		}
		setBubbleFuction &&
			setBubbleFuction(filter.type, () => setOpenIndex(parentIndex), filterContainerRef.current);
	}, [filter, parentIndex, filterContainerRef]);

	useEffect(() => {
		return () => {
			clearBubbleFunctionRef && clearBubbleFunctionRef();
		};
	}, []);
	useEffect(() => {
		if (deviceScreen === ScreenType.TABLET) {
			if (currentFilterOpenIndex !== undefined) {
				setOpenIndex(currentFilterOpenIndex);
			}
		} else {
			setOpenIndex(undefined);
		}
	}, [currentFilterOpenIndex, deviceScreen, setOpenIndex]);
	useEffect(() => {
		setIsDeskTopScreen(
			deviceScreen === ScreenType.LARGE_DESKTOP ||
				deviceScreen === ScreenType.MEDIUM_DESKTOP ||
				deviceScreen === ScreenType.SMALL_DESKTOP
		);
	}, [deviceScreen]);

	return (
		<div
			ref={filterContainerRef}
			data-testid='filter-container'
			className={`filter-container ${
				deviceViewMode === viewModeType.DESKTOP ? 'bottom-border' : ''
			} ${filter.type === FilterType.CATEGORY ? 'open-filter' : 'collapsed-filter'}`}
		>
			<div className='row'>
				<div
					className={`main-filter-container ${
						filter.type === FilterType.CATEGORY ? 'category-wrapper' : ''
					}`}
					onClick={
						!isDeskTopScreen && !isCategoryFilter
							? () => handleClick(parentIndex, filter.name)
							: () => {}
					}
				>
					<span className={`main-filter-name ${isCategoryFilter ? 'category-wrapper' : ''}`}>
						{filter.name}
					</span>
					<span
						data-testid='main-filter-name'
						className='icon-wrapper'
						onClick={
							isCategoryFilter
								? () => {}
								: () => {
										if (isCurrentUserAnonymous()) {
											setLockModalContent();
											return;
										}
										handleClick(parentIndex, filter.name);
								  }
						}
					>
						{isCurrentUserAnonymous() ? (
							<i className='ri-lock-line'></i>
						) : !isCategoryFilter ? (
							<button
								id={
									isOpen
										? `${filter.name?.replace(/\s+/g, '').trim().toLowerCase()}-opened`
										: `${filter.name?.replace(/\s+/g, '').trim().toLowerCase()}-closed`
								}
								type='button'
								className={`${
									isOpen
										? 'main-menu-close ri-arrow-down-s-line'
										: 'main-menu-open ri-arrow-right-s-line'
								}`}
							></button>
						) : null}
					</span>
				</div>
			</div>
			<div className='row'>
				{isOpen && (
					<div
						id={`filter-content${filter.id}`}
						ref={filterRef}
						className={`filter-content ${
							filter.type === FilterType.CATEGORY ? 'cat-margin-wrapper' : ''
						} `}
					>
						{filter?.filters.map((item: FilterVM, index: number) => {
							const isSelected = isMobile
								? pendingFilters?.find(f => f.id === item.id && f.name === item.name)?.isChecked ??
								  selectedFilters?.some(f => f.id === item.id && f.name === item.name)
								: selectedFilters?.some(f => f.id === item.id && f.name === item.name);
							return (
								<div key={index}>
									{item.children.length === 0 && item.type !== FilterType.CATEGORY ? (
										<>
											<div className={`filter-tooltip-container ${isSelected ? 'selected' : ''}`}>
												<CheckboxComponent
													checked={item.isChecked}
													label={item.name}
													style='primary'
													parentIndex={item.id}
													index={index}
													level={FilterLevel.FIRST}
													type={item.type}
													parentLength={filter.filters.length}
													isLocked={isCurrentUserAnonymous()}
													setLockModalContent={handleAnonymousClick}
													registerUserClickEvent={registerUserClickEvent}
													pendingFilters={pendingFilters}
													setPendingFilters={setPendingFilters}
													isNewFilter={false}
													isNonCategoryFilter={true}
												/>
												{item.toolTip?.description && (
													<i
														className={`ri-information-line tooltip-icon ${
															isSelected ? 'selected' : ''
														}`}
														id={`toltip-icon${item?.id || ''}`}
														onClick={() =>
															onTooltipClick(
																`${item.type}${item.id}`,
																`toltip-icon${item?.id || ''}`
															)
														}
													></i>
												)}
											</div>
											{item.toolTip?.description && (
												<FilterTooltipComponent
													id={`${item.type}${item.id}`}
													toolTip={item.toolTip}
												/>
											)}
										</>
									) : (
										<>
											{filter.type !== FilterType.CATEGORY && (
												<>
													{filterComponentState.multifilterItems.indexOf(filter) === -1 ? (
														<MenuItemComponent
															items={filter.filters}
															setLockModalContent={setLockModalContent}
															registerUserClickEvent={registerUserClickEvent}
														/>
													) : undefined}
													{filterComponentState.multiFilterHelper(
														filterComponentState.multifilterItems,
														filter
													)}
												</>
											)}
										</>
									)}
								</div>
							);
						})}
						{filter.type === FilterType.CATEGORY && (
							<CategoryFilterComponent
								filters={orderFiltersAlphabetically(filter.filters) || []}
								selectedFilters={selectedFilters}
								userState={user}
								setLockModalContent={setLockModalContent}
								registerUserClickEvent={registerUserClickEvent}
								pendingFilters={pendingFilters}
								setPendingFilters={setPendingFilters}
							/>
						)}
					</div>
				)}
			</div>
			{deviceViewMode === viewModeType.MOBILE && <div className='line'></div>}
		</div>
	);
};

// const FilterComponent: FC<IFilterComponentProps> = ({ onClose }) => {
// 	const dispatch = useAppDispatch();
// 	const deviceScreen = useSelector((state: RootState) => state.appState.deviceScreen);
// 	const { allFilters } = useSelector((state: RootState) => state.appState.filterState || {});
// 	const [openIndex, setOpenIndex] = useState<number | undefined>();
// 	const setCurrentFilterOpenIndex = (index: number) =>
// 		dispatch(FilterActions.setCurrentFilterOpenIndex(index));
// 	useEffect(() => {
// 		if (deviceScreen === ScreenType.TABLET && openIndex !== undefined) {
// 			setCurrentFilterOpenIndex(openIndex);
// 		}
// 	}, [openIndex, deviceScreen]);
// 	const isMobile = deviceScreen === ScreenType.MOBILE ? true : false;
// 	const [pendingFilters, setPendingFilters] = useState<FilterVM[]>([]);
// 	const [originalPendingFilters, setOriginalPendingFilters] = useState<FilterVM[]>([]);

// useEffect(() => {
//         if (pendingFilters.length > 0 && originalPendingFilters.length === 0) {
//             setOriginalPendingFilters(pendingFilters.map(f => ({ ...f })));
//         }
//     }, [pendingFilters]);

// 	const handleApplyFilters = () => {
// 		pendingFilters.forEach(filter => {
// 			if (filter.isChecked) {
// 				dispatch(FilterActions.addFilter(filter));
// 			} else {
// 				dispatch(FilterActions.removeFilter(filter, true)); // true = isMobile
// 			}
// 		});
// 		setPendingFilters([]);
// 		onClose()
// 	};

// 	const [disableButton, setDisableButton] = useState<boolean>(true);

// const selectedFilters = useSelector(
//     (state: RootState) => state.appState.filterState?.selectedFilters
// );

// useEffect(() => {
//     if (!pendingFilters || !selectedFilters) {
//         setDisableButton(true);
//         return;
//     }

//     const hasChanges = pendingFilters.some(pending => {
//         const original = selectedFilters.find(sel => sel.id === pending.id);

//         // If filter is new or its checked state has changed
//         return !original || original.isChecked !== pending.isChecked;
//     });

//     setDisableButton(!hasChanges);
// }, [pendingFilters, selectedFilters]);

// 	return (
// 		<div className="page-container">
// 			<div className="filters-scrollable">
// 				<div className="filters-container">
// 					{allFilters && allFilters.length > 0 && (
// 						<>
// 							{allFilters.map((filter, index) => (
// 								<MainFilterComponent
// 									key={index}
// 									filter={filter}
// 									parentIndex={index}
// 									setOpenIndex={setOpenIndex}
// 									openIndex={openIndex}
// 									deviceScreen={deviceScreen}
// 									isMobile={isMobile}
// 									pendingFilters={pendingFilters}
// 									setPendingFilters={setPendingFilters}
// 									selectedFilters={selectedFilters}
// 								/>
// 							))}
// 						</>
// 					)}
// 				</div>
// 			</div>
// 			{isMobile &&
// 				<div className={`bottom ${disableButton ? 'disabled-style' : ''}`}>
// 					<div className="filter-buttons">
// 						<button className='apply-filter'
// 							onClick={handleApplyFilters}
// 							disabled={disableButton}
// 						>Apply Filters</button>
// 						<div className="cancel-filter" onClick={onClose} >Cancel</div>
// 					</div>
// 				</div>}
// 		</div>
// 	);
// };

// export default FilterComponent;

const FilterComponent: FC<IFilterComponentProps> = ({
	onClose,
	currentCategory,
	setCurrentCategory,
	productsPageUrl,
	setLockModalContent,
	registerUserClickEvent,
	clearAllFilters
}) => {
	const dispatch = useAppDispatch();
	const deviceScreen = useSelector((state: RootState) => state.appState.deviceScreen);
	const { allFilters } = useSelector((state: RootState) => state.appState.filterState || {});
	const selectedFilters = useSelector(
		(state: RootState) => state.appState.filterState?.selectedFilters
	);

	const [openIndex, setOpenIndex] = useState<number | undefined>();
	const [pendingFilters, setPendingFilters] = useState<FilterVM[]>([]);
	const [disableButton, setDisableButton] = useState<boolean>(true);

	const isMobile = deviceScreen === ScreenType.MOBILE;

	const setCurrentFilterOpenIndex = (index: number) =>
		dispatch(FilterActions.setCurrentFilterOpenIndex(index));

	useEffect(() => {
		if (deviceScreen === ScreenType.TABLET && openIndex !== undefined) {
			setCurrentFilterOpenIndex(openIndex);
		}
	}, [openIndex, deviceScreen]);

	// Enable Apply button only if there's a net change from Redux state
	useEffect(() => {
		if (!pendingFilters || !selectedFilters) {
			setDisableButton(true);
			return;
		}

		const hasChanges = pendingFilters.some(pending => {
			const original = selectedFilters.find(sel => sel.id === pending.id);
			// If not in selectedFilters, treat as unchecked originally
			const originalChecked = original?.isChecked ?? false;
			return originalChecked !== pending.isChecked;
		});

		setDisableButton(!hasChanges);
	}, [pendingFilters, selectedFilters]);

	const handleApplyFilters = () => {
		pendingFilters.forEach(filter => {
			if (filter.isChecked) {
				dispatch(FilterActions.addFilter(filter));
			} else {
				dispatch(FilterActions.removeFilter(filter, true)); // true = isMobile
			}
		});
		setPendingFilters([]);
		onClose();
	};
	const storeState = useSelector((state: RootState) => state.appState.storeState);
	return (
		<div className='page-container'>
			<div className='filters-scrollable'>
				<div className='filters-container'>
					<div className='newfilter-container'>
						{/* SHOW PARTICIPATION */}
						<div className='event-container'>
							<div className='newfilter-title'>
								<div className='newfilter-partcipation-title'>SHOW PARTICIPATION</div>
								{isCurrentUserAnonymous() && (
									<button className='lock' onClick={setLockModalContent} type='button'>
										<i className='ri-lock-line' />
									</button>
								)}
							</div>

							<div className='filter-elements'>
								<ShowParticipationFilterComponent
									selectedFilters={selectedFilters}
									pendingFilters={pendingFilters}
									setPendingFilters={setPendingFilters}
									setLockModalContent={setLockModalContent}
									registerUserClickEvent={registerUserClickEvent}
								/>
							</div>
						</div>

						{/* TRENDING */}
						<div className='trending-container'>
							<div className='trending-title'>
								TRENDING{' '}
								{isCurrentUserAnonymous() && (
									<button className='lock' onClick={setLockModalContent} type='button'>
										<i className='ri-lock-line' />
									</button>
								)}
							</div>

							{/* <div className='filter-item'>
								<span className='dot'></span>
								<span className='label'>New To Market</span>
				
								<LabelForms
									text=''
									icon={<i className='ri-question-line' />}
									toolTipText={<>Products or brands that launched within the last year.</>}
									toolTipPosition='right'
								></LabelForms>
							</div>

							<div className='filter-item'>
								<span className='dot'></span>
								<span className='label'>New To Beacon</span>
								<LabelForms
									text=''
									icon={<i className='ri-question-line' />}
									toolTipText={<>Products added to Beacon Discovery within the last 30 days.</>}
									toolTipPosition='right'
								></LabelForms>
							</div>

							<div className='filter-item'>
								<span className='dot'></span>
								<span className='label'>NEXTY Finalists</span>
								<LabelForms
									text=''
									icon={<i className='ri-question-line' />}
									toolTipText={
										<>
											The NEXTY Awards recognize the most progressive, innovative, inspiring and
											trustworthy products in the natural products industry.
										</>
									}
									toolTipPosition='right'
								></LabelForms>
							</div>   */}

							<WhatsNewFilterElementsComponent
								filters={storeState?.filters?.whatsNew || []}
								currentCategory={currentCategory}
								setCurrentCategory={setCurrentCategory}
								productsPageUrl={productsPageUrl}
								selectedFilters={selectedFilters}
								setLockModalContent={setLockModalContent}
								registerUserClickEvent={registerUserClickEvent}
								clearAllFilters={clearAllFilters}
								pendingFilters={pendingFilters}
								setPendingFilters={setPendingFilters}
								isMobile={isMobile}
							/>
						</div>
					</div>
					{allFilters && allFilters.length > 0 && (
						<>
							{allFilters.map((filter, index) => (
								<>
									<MainFilterComponent
										key={filter.id ?? index}
										filter={filter}
										parentIndex={index}
										setOpenIndex={setOpenIndex}
										openIndex={openIndex}
										deviceScreen={deviceScreen}
										isMobile={isMobile}
										pendingFilters={pendingFilters}
										setPendingFilters={setPendingFilters}
										selectedFilters={selectedFilters}
									/>

									{filter.type === FilterType.CATEGORY && (
										<div className='attribute-certification-section'>
											<span className='text'>Attributes & Certifications</span>
										</div>
									)}
								</>
							))}
						</>
					)}
				</div>
			</div>

			{isMobile && (
				<div className={`bottom ${disableButton ? 'disabled-style' : ''}`}>
					<div className='filter-buttons'>
						<button
							className='apply-filter'
							onClick={handleApplyFilters}
							disabled={disableButton}
							type='button'
						>
							Apply Filters
						</button>
						<div className='cancel-filter' onClick={onClose}>
							Cancel
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default FilterComponent;
